import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CalendarDays, Clock, Users, Plus } from 'lucide-react';
import { formatTime } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoomSchedulePage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [roomConfig, setRoomConfig] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [scheduleData, setScheduleData] = useState({});
  const [loading, setLoading] = useState(false);

  // Get employee's location for default filtering
  const employeeLocation = employee?.location || '';

  useEffect(() => {
    const fetchRoomConfig = async () => {
      try {
        const res = await fetch(`${API}/rooms/config`, {
          headers: getAuthHeader(),
        });
        if (res.ok) {
          const data = await res.json();
          // Flatten the config: merge small+large+all into a single array per location
          const flatConfig = {};
          const config = data.config || data;
          for (const [loc, rooms] of Object.entries(config)) {
            flatConfig[loc] = [];
            if (rooms.all) flatConfig[loc].push(...rooms.all);
            if (rooms.small) flatConfig[loc].push(...rooms.small);
            if (rooms.large) flatConfig[loc].push(...rooms.large);
          }
          setRoomConfig(flatConfig);
          
          // Auto-select location based on employee location
          const empLoc = employeeLocation;
          if (empLoc && flatConfig[empLoc]) {
            setSelectedLocation(empLoc);
          } else {
            const firstLoc = Object.keys(flatConfig)[0];
            if (firstLoc) setSelectedLocation(firstLoc);
          }
        }
      } catch (error) {
        console.error('Fetch room config error:', error);
      }
    };
    fetchRoomConfig();
  }, [getAuthHeader, employeeLocation]);

  const fetchSchedule = useCallback(async () => {
    if (!selectedLocation || !selectedDate) return;
    
    const rooms = roomConfig[selectedLocation] || [];
    if (rooms.length === 0) return;
    
    setLoading(true);
    const schedules = {};
    
    try {
      for (const room of rooms) {
        const res = await fetch(
          `${API}/rooms/schedule?location=${encodeURIComponent(selectedLocation)}&room=${encodeURIComponent(room)}&date=${selectedDate}`,
          { headers: getAuthHeader(),  }
        );
        if (res.ok) {
          schedules[room] = await res.json();
        } else {
          schedules[room] = [];
        }
      }
      setScheduleData(schedules);
    } catch (error) {
      console.error('Fetch schedule error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate, roomConfig, getAuthHeader]);

  useEffect(() => {
    if (selectedLocation && selectedDate) {
      fetchSchedule();
    }
  }, [selectedLocation, selectedDate, fetchSchedule]);

  const rooms = roomConfig[selectedLocation] || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="room-schedule-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">View Room Schedule</h1>
            <p className="text-muted-foreground">Check meeting room availability and bookings</p>
          </div>
          <Button
            onClick={() => navigate('/tickets/create?module=Admin&type=Meeting%20Room%20Booking&locked=1')}
            data-testid="book-room-btn"
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Book Room
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger data-testid="schedule-location">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(roomConfig).map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="schedule-date"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchSchedule} className="w-full" data-testid="refresh-schedule-btn">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Refresh Schedule
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => (
              <Card key={i}>
                <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
                <CardContent><Skeleton className="h-20 w-full" /></CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rooms.map((room) => {
              const bookings = scheduleData[room] || [];
              const sortedBookings = [...bookings].sort((a, b) => 
                (a.room_start_time || '').localeCompare(b.room_start_time || '')
              );
              
              return (
                <Card key={room} data-testid={`room-card-${room.replace(/\s+/g, '-')}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>{room}</span>
                      {bookings.length === 0 ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Available
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sortedBookings.length === 0 ? (
                      <p className="text-sm text-green-600">No bookings - room is available all day</p>
                    ) : (
                      <div className="space-y-2">
                        {sortedBookings.map((booking, i) => {
                          const cat = booking.room_meeting_category || '';
                          const isExternal = cat && cat !== 'Internal';
                          // External meetings (Client/Dealer/Supplier/Partner) get an amber-tinted row + badge
                          const rowCls = isExternal
                            ? 'border-amber-300 bg-amber-50/70 dark:bg-amber-950/30'
                            : 'bg-muted/50';
                          const catBadgeCls = !cat
                            ? ''
                            : isExternal
                              ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300'
                              : 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800/60 dark:text-slate-300';
                          return (
                            <div
                              key={i}
                              className={`flex flex-wrap items-center justify-between gap-2 p-2 rounded-md border ${rowCls}`}
                              data-testid={`room-booking-${booking.ticket_id}`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                <div className="flex items-center gap-1 text-sm font-medium">
                                  <Clock className="w-3 h-3" />
                                  {formatTime(booking.room_start_time)} – {formatTime(booking.room_end_time)}
                                </div>
                                <span className="text-sm text-muted-foreground truncate max-w-[140px]">
                                  {booking.requester_name}
                                </span>
                              </div>
                              {cat && (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${catBadgeCls}`}
                                  data-testid={`booking-category-${booking.ticket_id}`}
                                >
                                  {cat}
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {rooms.length === 0 && !loading && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Select a location to view room schedules
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
